import React from "react";
import { Sparkles, Loader2, CheckCircle2, BookOpen } from "lucide-react";
import { Modal } from "../../common/Modal";
import { Requirement, LibraryAnswerMatch } from "../../../types";

export interface BatchAutoDraftCandidate {
  requirement: Requirement;
  topMatch: LibraryAnswerMatch;
}

interface BatchAutoDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  eligibleCandidates: BatchAutoDraftCandidate[];
  onExecute: () => Promise<void>;
  isProcessing: boolean;
  progress: { current: number; total: number } | null;
}

export const BatchAutoDraftModal: React.FC<BatchAutoDraftModalProps> = ({
  isOpen,
  onClose,
  eligibleCandidates,
  onExecute,
  isProcessing,
  progress,
}) => {
  const totalCount = eligibleCandidates.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={isProcessing ? () => {} : onClose}
      title="Batch Auto-Draft First Baseline"
      maxWidth="2xl"
    >
      <div className="space-y-4 text-xs">
        {totalCount === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center dark:border-slate-800">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <h3 className="mt-2 text-sm font-bold text-slate-800 dark:text-slate-200">
              No Unanswered High-Confidence Matches Found
            </h3>
            <p className="mt-1 text-slate-500 dark:text-slate-400">
              All requirements with &ge;75% library similarity have already been drafted, or no
              high-confidence matches were returned. Try running &ldquo;Match All with Answer
              Library&rdquo; first.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-animate rounded-lg bg-slate-100 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 dark:border-teal-900/60 dark:bg-teal-950/30">
              <div className="flex items-start gap-2.5">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#2ba5b5]" />
                <div className="space-y-1">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Accelerate First-Draft Assembly ({totalCount} Requirements)
                  </p>
                  <p className="text-slate-600 dark:text-slate-300">
                    The engine found <strong>{totalCount}</strong> unanswered requirements with
                    high-confidence (&ge;75%) precedents in your Answer Library. Running auto-draft
                    will generate grounded narrative drafts, link source citations, and highlight
                    any remaining action items.
                  </p>
                </div>
              </div>
            </div>

            {/* Candidates List */}
            <div className="max-h-60 overflow-y-auto space-y-2 rounded-xl border border-slate-200 p-2 dark:border-slate-800">
              {eligibleCandidates.map(({ requirement, topMatch }) => (
                <div
                  key={requirement.id}
                  className="flex items-center justify-between rounded-lg bg-white p-2.5 shadow-2xs border border-slate-100 dark:bg-slate-900 dark:border-slate-800"
                >
                  <div className="min-w-0 pr-3">
                    <p className="font-semibold text-slate-900 dark:text-white truncate">
                      {requirement.sectionRef ? `${requirement.sectionRef} ` : ""}
                      {requirement.title}
                    </p>
                    <p className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                      <BookOpen className="h-3 w-3 text-slate-400" />
                      <span className="truncate">Top: {topMatch.title}</span>
                      {topMatch.wonBidCount && topMatch.wonBidCount > 0 ? (
                        <span className="shrink-0 text-amber-600 font-bold">🏆 Won</span>
                      ) : null}
                    </p>
                  </div>

                  <span className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-bold text-[#2ba5b5] dark:bg-teal-950/50">
                    {Math.round(topMatch.matchScore * 100)}% Match
                  </span>
                </div>
              ))}
            </div>

            {/* Progress Display */}
            {isProcessing && progress && (
              <div className="space-y-1.5 pt-2">
                <div className="flex justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-[#2ba5b5]" />
                    <span>
                      Auto-drafting requirement {progress.current} of {progress.total}...
                    </span>
                  </span>
                  <span>{Math.round((progress.current / progress.total) * 100)}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div
                    className="h-full bg-[#2ba5b5] transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="btn-animate rounded-lg border border-slate-200 bg-white px-3.5 py-2 font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={onExecute}
                disabled={isProcessing}
                className="btn-animate inline-flex items-center gap-2 rounded-lg bg-[#2ba5b5] px-4 py-2 font-semibold text-white shadow-xs hover:bg-[#238997] disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Auto-Drafting...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Start Auto-Draft ({totalCount})</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
